import React, { useState, useEffect } from 'react';
import { GetCMSContent } from 'aldoo-ra/CMS/api';
import DropdownSelect from 'aldoo-ra/DropdownSelect';

const parseConfig = (value) => {
  if (!value) return { source: 'custom', menuName: '', items: [] };
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return { source: 'custom', menuName: '', items: [] }; }
  }
  return value;
};

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-1 text-[10px] font-semibold rounded transition-colors ${
      active
        ? 'bg-white shadow text-blue-600 border border-slate-200'
        : 'text-slate-400 hover:text-slate-600'
    }`}
  >
    {children}
  </button>
);

const ItemRow = ({ item, index, onChange, onRemove }) => (
  <div className="flex items-center gap-1 mb-1">
    <input
      className="flex-1 rounded px-1.5 py-0.5 text-[10px] border border-slate-200 bg-white outline-none focus:border-blue-400 min-w-0"
      placeholder="Label"
      value={item.label || ''}
      onChange={(e) => onChange(index, { ...item, label: e.target.value })}
    />
    <input
      className="flex-1 rounded px-1.5 py-0.5 text-[10px] border border-slate-200 bg-white outline-none focus:border-blue-400 min-w-0"
      placeholder="Link"
      value={item.link || ''}
      onChange={(e) => onChange(index, { ...item, link: e.target.value })}
    />
    <button
      onClick={() => onRemove(index)}
      className="text-slate-300 hover:text-red-400 text-xs flex-shrink-0 px-0.5"
      title="Remove item"
    >
      ✕
    </button>
  </div>
);

const MenuPickerField = ({ value, onChange }) => {
  const config = parseConfig(value);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    GetCMSContent({ match: { 'content.type': 'MenuItem' }, itemsPerPage: 200 })
      .then((result) => {
        const names = (result?.data || [])
          .map((d) => d?.content?.name)
          .filter(Boolean);
        setMenus(names);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (patch) => onChange({ ...config, ...patch });

  const setSource = (source) => update({ source });

  const handleItemChange = (index, newItem) => {
    const items = [...(config.items || [])];
    items[index] = newItem;
    update({ items });
  };

  const handleItemRemove = (index) => {
    update({ items: (config.items || []).filter((_, i) => i !== index) });
  };

  const handleAddItem = () => {
    update({ items: [...(config.items || []), { label: 'New Item', link: '#' }] });
  };

  const isCms = config.source === 'cms';

  return (
    <div className="space-y-2">
      {/* Source toggle */}
      <div className="flex bg-slate-100 p-0.5 rounded">
        <TabBtn active={!isCms} onClick={() => setSource('custom')}>Custom</TabBtn>
        <TabBtn active={isCms} onClick={() => setSource('cms')}>CMS Menu</TabBtn>
      </div>

      {isCms ? (
        <DropdownSelect
          variant="admin"
          searchEnabled
          showCheckbox={false}
          placeholder={loading ? 'Loading…' : 'Search menus…'}
          options={menus.map((name) => ({ label: name, value: name }))}
          value={config.menuName ? { label: config.menuName, value: config.menuName } : null}
          onChange={(selected) => update({ menuName: selected?.[0]?.value || selected?.value || '' })}
        />
      ) : (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] text-slate-400 uppercase tracking-wider">Items</span>
            <span className="text-[9px] text-slate-400">label · link</span>
          </div>

          {(config.items || []).length === 0 && (
            <p className="text-[10px] text-slate-400 italic mb-1.5">No items yet.</p>
          )}

          {(config.items || []).map((item, i) => (
            <ItemRow
              key={i}
              item={item}
              index={i}
              onChange={handleItemChange}
              onRemove={handleItemRemove}
            />
          ))}

          <button
            onClick={handleAddItem}
            className="mt-1 w-full text-[10px] text-blue-500 hover:text-blue-700 border border-dashed border-blue-200 hover:border-blue-400 rounded py-0.5 transition-colors"
          >
            + Add Item
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuPickerField;
